// src/components/programs/ProgramForm/Step1Sections/utils/DragDropContext.js

/**
 * Drag & Drop Context and Utilities
 * Handles drag operations between Products Panel and Days
 */

// Drag Types
export const DRAG_TYPES = {
    PRODUCT_NEW: 'product_new',        // From Products Panel
    PRODUCT_EXISTING: 'product_existing', // From Day to Day
    PRODUCT_REORDER: 'product_reorder',   // Within same Day
};

/**
 * Create drag data for new product from panel
 */
export const createProductDragData = (product) => {
    return {
        type: DRAG_TYPES.PRODUCT_NEW,
        product: product,
    };
};

/**
 * Create drag data for existing product instance
 */
export const createInstanceDragData = (product, dayIndex, isCopy = false) => {
    return {
        type: isCopy ? DRAG_TYPES.PRODUCT_EXISTING : DRAG_TYPES.PRODUCT_REORDER,
        product: product,
        sourceDayIndex: dayIndex,
        isCopy: isCopy,
    };
};

/**
 * Parse drag data from dataTransfer
 */
export const parseDragData = (dataTransfer) => {
    try {
        const data = dataTransfer.getData('application/json');
        return data ? JSON.parse(data) : null;
    } catch (error) {
        console.error('Error parsing drag data:', error);
        return null;
    }
};

/**
 * Set drag data to dataTransfer
 */
export const setDragData = (dataTransfer, data) => {
    try {
        dataTransfer.setData('application/json', JSON.stringify(data));
        dataTransfer.effectAllowed = data.isCopy ? 'copy' : 'move';
    } catch (error) {
        console.error('Error setting drag data:', error);
    }
};

/**
 * Extract the city id from a product payload
 */
const getProductCityId = (product) => {
    if (!product) return null;
    if (product.city_id !== undefined && product.city_id !== null) return product.city_id;
    if (product.city?.id !== undefined && product.city?.id !== null) return product.city.id;
    if (product.locations?.id !== undefined && product.locations?.id !== null) return product.locations.id;
    if (typeof product.city === 'object' && product.city !== null && product.city.id !== undefined) return product.city.id;
    return null;
};

/**
 * Create product instance from product data
 */
export const createProductInstance = (product, order = 1) => {
    const pricingCode = product.pricing_code || product.pricing_unit?.code || product.product_snapshot?.pricing_unit;

    // Check if it's a free day product
    const isFreeDay = product.productType?.code === 'free_day';

    // Default dynamic fields
    const dynamicFields = {};
    if (pricingCode === 'per_night') {
        // default nights to 1 for per_night products
        dynamicFields.nights = 1;
    }
    if (pricingCode === 'per_person') {
        const cityId = getProductCityId(product);
        if (cityId !== null && cityId !== undefined) {
            dynamicFields.city_id = cityId;
        }
    }
    if (product.productType?.code === 'flight' || product.product_type?.code === 'flight') {
        dynamicFields.flight_details = product.flight_details ? { ...product.flight_details } : {
            airline_id: null,
            departure_airport_id: null,
            arrival_airport_id: null,
            departure_date: null,
            arrival_date: null,
            departure_time: null,
            arrival_time: null,
            flight_number: null,
            pnr: null,
            baggage_weight: null,
            baggage_unit: "kg",
        };
    }

    return {
        instance_id: `temp_${Date.now()}_${Math.random()}`,
        product_snapshot: {
            product_id: product.id,
            title: product.title,
            category: product.category,
            pricing_unit: product.pricing_code,
            pricing_behavior: product.pricing_behavior || null, // ✅ Retain pricing_behavior
            original_price: product.price,
            original_cost_price: product.cost_price || 0,
            city: product.locations || null,
            productType: product.productType || product.product_type || null,
            product_type: product.product_type || product.productType || null,
            pax_type_prices: product.pax_type_prices || [], // ✅ Add per-pax prices to snapshot
        },
        selected_supplier: isFreeDay ? null : (product.suppliers?.[0] || null), // No supplier for free day
        selected_service: product.service || null,
        pricing_behavior: product.pricing_behavior || null, // ✅ Retain pricing_behavior at root
        order: order,
        pricing: {
            price: isFreeDay ? 0 : product.price, // Price is 0 for free day
            cost_price: isFreeDay ? 0 : (product.cost_price || 0), // Cost is 0 for free day
            custom_price: null,
            is_price_edited: false,
            discount_type: isFreeDay ? 'zero' : 'zero', // Always zero discount for free day
            discount_value: null,
        },
        dynamic_fields: dynamicFields,
        calculated: {
            final_price_per_unit: isFreeDay ? 0 : product.price,
            final_cost_per_unit: isFreeDay ? 0 : (product.cost_price || 0),
            quantity: 0,
            total_price: 0,
            total_cost: 0,
        },
    };
};

/**
 * Clone existing product instance with new ID
 */
export const cloneProductInstance = (instance, order = 1) => {
    return {
        ...instance,
        instance_id: `temp_${Date.now()}_${Math.random()}`,
        order: order,
        pricing_behavior: instance.pricing_behavior || instance.product_snapshot?.pricing_behavior || null, // ✅ Ensure it's preserved on clone
        product_snapshot: {
            ...instance.product_snapshot,
            pricing_behavior: instance.pricing_behavior || instance.product_snapshot?.pricing_behavior || null,
            productType: instance.product_snapshot?.productType, // Ensure productType is copied
        },
    };
};

/**
 * Check if product already exists in day
 */
export const isDuplicateProduct = (day, productId) => {
    return day.products.some(
        (p) => p.product_snapshot.product_id === productId
    );
};

/**
 * Get drop effect based on modifier keys
 */
export const getDropEffect = (event) => {
    // Ctrl (Windows/Linux) or Cmd (Mac) = Copy
    if (event.ctrlKey || event.metaKey) {
        return 'copy';
    }
    return 'move';
};

/**
 * Check if event is copy operation
 */
export const isCopyOperation = (event) => {
    return event.ctrlKey || event.metaKey;
};

const dragDropUtils = {
    DRAG_TYPES,
    createProductDragData,
    createInstanceDragData,
    parseDragData,
    setDragData,
    createProductInstance,
    cloneProductInstance,
    isDuplicateProduct,
    getDropEffect,
    isCopyOperation,
};

export default dragDropUtils;
